import React, { useEffect, useState, useRef, useMemo } from "react";
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import FilterModal from "./components/FilterModal";
import GroupModal from "./components/GroupModal";
import CalendarModal from "./components/CalendarModal";
import CallPlayer from "./CallPlayer";
import Activity from "./Activity";
import { useSelector, useDispatch } from 'react-redux'
import { setFilter, setDateFilter, setUnitFilter, setAllFilter, setFilterTimeframe } from "../features/callPlayer/callPlayerSlice";
import { getCalls, addCall, getOlderCalls, getNewerCalls } from "../features/calls/callsSlice";
import { useGetGroupsQuery, useGetSystemsQuery, useGetMeQuery, useLogoutMutation } from '../features/api/apiSlice'
import NotificationBell from "../Notifications/NotificationBell";
import {
  Label,
  Menu,
  Icon,
  Input,
  Sidebar,
  Message,
  Sticky
} from "semantic-ui-react";
import "./CallPlayer.css";
import queryString from '../query-string';
import io from 'socket.io-client';



// withCredentials so the session cookie is sent on the handshake, letting the
// backend associate this socket with the logged-in user for bell notifications.
const socket = io(process.env.REACT_APP_BACKEND_SERVER, { withCredentials: true });

// Reloading the list throws away every page of older calls the user has
// scrolled through, so after a dropped connection we only do it when they are
// sitting at the top of the feed, where replacing the list is invisible.
const SCROLL_TOP_THRESHOLD = 8;

// ----------------------------------------------------
function Calls(props) {

  const { shortName } = useParams();
  const { data: allSystems, isSuccess } = useGetSystemsQuery();
  const { data: me } = useGetMeQuery();
  const [logout] = useLogoutMutation();
  const { data: groupsData, isSuccess: isGroupsSuccess } = useGetGroupsQuery(shortName);
  const { loading: callsLoading, data: callsData } = useSelector((state) => state.calls);

  const [autoPlay, setAutoPlay] = useState(true);
  const [currentCall, setCurrentCall] = useState(false);
  const [urlOptions, setUrlOptions] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [groupVisible, setGroupVisible] = useState(false);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [activityVisible, setActivityVisible] = useState(false);
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [selectCallId, setSelectCallId] = useState(false);
  const [initialCallId, setInitialCallId] = useState(false);
  const [live, setLive] = useState(true);
  const [unitSearch, setUnitSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const wasConnectedRef = useRef(true); // so the first connect isn't seen as a reconnect
  const shouldPlayAddCallRef = useRef(); // we need to do this to make the current value of isPlaying available in the socket message callback
  shouldPlayAddCallRef.current = (!isPlaying && autoPlay) ? true : false;
  const uri = queryString.parse(useLocation().search);
  const filterType = useSelector((state) => state.callPlayer.filterType);
  const filterGroupId = useSelector((state) => state.callPlayer.filterGroupId);
  const filterTalkgroups = useSelector((state) => state.callPlayer.filterTalkgroups);
  const filterUnit = useSelector((state) => state.callPlayer.filterUnit);
  const filterRows = useSelector((state) => state.callPlayer.filterRows);
  const filterTimeframe = useSelector((state) => state.callPlayer.filterTimeframe);
  const filterStarred = useSelector((state) => state.callPlayer.filterStarred);
  const filterDate = useSelector((state) => state.callPlayer.filterDate);
  //const live = useSelector((state) => state.callPlayer.live);

  const pathname = useLocation().pathname;

  let currentCallId = false;
  //let system = false;

  if (currentCall) {
    currentCallId = currentCall._id;
  }

  let system = useMemo(() => {
    if (allSystems) {
      return allSystems.systems.find((system) => system.shortName === shortName)
    } else {
      return false;
    }
  }, [allSystems, shortName])

  const handlePusherClick = () => {
    if (sidebarOpened) setSidebarOpened(false);
  }

  // Top-bar RID / alias search. An empty term drops back to the "all" filter.
  const handleUnitSearchSubmit = () => {
    const term = unitSearch.trim();
    if (term) {
      dispatch(setUnitFilter(term));
    } else {
      dispatch(setAllFilter());
    }
  }

  const handleSidebarToggle = () => setSidebarOpened(!sidebarOpened);
  const handleFilterToggle = () => setFilterVisible(!filterVisible);
  const handleCalendarToggle = () => setCalendarVisible(!calendarVisible);
  const handleActivityToggle = () => setActivityVisible(!activityVisible);
  const handleActivityNavigate = (tgNum, timestamp) =>{

    let filter = {
      filterDate: timestamp,
      filterType: 2,  // this is "all"
      filterTalkgroups: [tgNum],
      filterGroupId: false,
      filterStarred: false,
      shortName: shortName
    };

    dispatch(setFilter(filter));
    setLive(false);
    stopSocket();
    setActivityVisible(false)
  }

  const getFilterDescription = () => {
    let filter = { type: 'all', code: "", filterStarred: false };

    switch (filterType) {
      case 1:
        filter.type = "group";
        filter.code = filterGroupId
        break;
      case 2:
        filter.type = "talkgroup";
        filter.code = filterTalkgroups
        break;
      case 3:
        filter.type = "unit";
        filter.code = filterUnit
        break;
      default:
      case 0:
        filter.type = "all";
        filter.code = ""
    }
    filter.filterStarred = filterStarred
    return filter;
  }


  const handleSocketMessage = (data) => {
    const message = JSON.parse(data)
    switch (message.type) {
      case 'calls':
        setSelectCallId(message._id);
        dispatch(addCall(message));
        const time = new Date(message.time);
        console.log("Got Socket Message: " + message._id + " Start Time: " + time.toLocaleTimeString());
        break
      default:
        break
    }
  }

  const startSocket = () => {
    const filter = getFilterDescription();
    // A stacked, SIEM-style filter is sent as its own spec; the backend compiles
    // it into a predicate and ignores the legacy filterType/filterCode fields.
    const hasTimeframe = filterTimeframe && (filterTimeframe.from || filterTimeframe.to);
    const advancedSpec = ((filterRows && filterRows.length > 0) || hasTimeframe)
      ? JSON.stringify({
          rows: filterRows || [],
          ...(hasTimeframe ? { timeframe: { from: filterTimeframe.from || 0, to: filterTimeframe.to || 0 } } : {})
        })
      : undefined;
    console.log("Starting Socket - Filter Code: " + filter.code + " Filter Type: " + filter.type + (advancedSpec ? " (advanced)" : ""));
    socket.emit("start", {
      filterCode: filter.code,
      filterType: filter.type,
      filterName: "OpenMHz",
      filterStarred: filter.filterStarred,
      filter: advancedSpec,
      shortName: shortName
    });
  }

  const stopSocket = () => {
    console.log("Stopping Socket")
    socket.emit("stop");
  }

  const updateUri = () => {
    let search = "?"
    switch (filterType) {

      case 1:
        search = search + `filter-type=group&filter-code=${filterGroupId}`;
        break;
      case 2:
        search = search + `filter-type=talkgroup&filter-code=${filterTalkgroups}`;
        break;
      case 3:
        search = search + `filter-type=unit&filter-code=${encodeURIComponent(filterUnit)}`;
        break;
      default:
      case 0:
        break;

    }

    const hasTimeframe = filterTimeframe && (filterTimeframe.from || filterTimeframe.to);
    if ((filterRows && filterRows.length > 0) || hasTimeframe) {
      const spec = { rows: filterRows || [] };
      if (hasTimeframe) {
        spec.timeframe = { from: filterTimeframe.from || 0, to: filterTimeframe.to || 0 };
      }
      if (search.length !== 1) {
        search = search + '&';
      }
      search = search + `filter=${encodeURIComponent(JSON.stringify(spec))}`;
    }

    if (initialCallId) {
      if (search.length !== 1) {
        search = search + '&';
      }
      search = search + `call-id=${initialCallId}`;
    }
    if (filterDate) {
      if (search.length !== 1) {
        search = search + '&';
      }
      search = search + `time=${filterDate}`;
    }

    if (filterStarred) {
      if (search.length !== 1) {
        search = search + '&';
      }
      search = search + `starred=true`;
    }
    navigate(pathname + search, { replace: true });

  }

  // Going back to live clears the archive date; the effects below reload the
  // list and re-subscribe the socket off the back of that.
  const handleLiveToggle = () => {
    if (!live) {
      dispatch(setDateFilter(false));
      dispatch(setFilterTimeframe({ from: false, to: false }));
      setLive(true);
    }
  }

  const handleCalendarClose = (didUpdate) => {
    setCalendarVisible(!calendarVisible);

    if (didUpdate) {
      setLive(false);
      stopSocket();
    }
  }

  const handleGroupClose = (didUpdate) => {
    setGroupVisible(!groupVisible);
  }

  const handleFilterClose = (didUpdate) => {
    setFilterVisible(!filterVisible);
  }


  const handleNewer = () => {
    if (!live) {
      dispatch(getNewerCalls({}));
    }
  }

  const handleOlder = () => {
    dispatch(getOlderCalls({}));
  }

  const setStateFromUri = async () => {
  
    let filter = {
      filterDate: false,
      filterType: 0,  // this is "all"
      filterTalkgroups: [],
      filterGroupId: false,
      filterUnit: "",
      filterRows: [],
      filterTimeframe: { from: false, to: false },
      filterStarred: false,
      filterCallId: false,
      shortName: shortName
    };

    // A shared advanced-filter URL: one JSON param holding the stacked rows and
    // an optional From/To range. It supersedes the legacy filter-type below.
    if (uri.hasOwnProperty('filter')) {
      try {
        const parsed = JSON.parse(uri['filter']);
        if (parsed && Array.isArray(parsed.rows)) {
          filter.filterRows = parsed.rows;
        }
        if (parsed && parsed.timeframe && (parsed.timeframe.from || parsed.timeframe.to)) {
          filter.filterTimeframe = {
            from: parsed.timeframe.from || false,
            to: parsed.timeframe.to || false
          };
          setLive(false);
        }
      } catch (err) {
        console.warn("Could not parse filter from URL: " + err);
      }
      if (!urlOptions) setUrlOptions(true);
    }

    // is there a star filter?
    if (uri.hasOwnProperty('starred')) {
      const starred = uri['starred'];
      filter.filterStarred = starred === 'true' ? true : false;
      if (!urlOptions) setUrlOptions(true);
    }

    // is there a time based filter?
    if (uri.hasOwnProperty('time')) {
      const date = new Date(parseInt(uri['time']));
      filter.filterDate = date.getTime();
      setLive(false);
      //filter.live = false;
      if (!urlOptions) setUrlOptions(true);
    }

    // is this just for one call?
    if (uri.hasOwnProperty('call-id')) {
      const _id = uri['call-id'];
      setInitialCallId(_id);
      filter.filterCallId = false;
      setAutoPlay(false);
      setLive(false);
      if (!urlOptions) setUrlOptions(true);

      // Anchor the archive page to the call's actual time, not the URI's time
      // hint. The hint can be off (notification-bell links to old hits only
      // know when the notification fired, which on a busy system can be >50
      // calls after the call started) — and if it is, the /older page fetched
      // from it won't contain the call, so it never highlights or plays.
      try {
        const res = await fetch(process.env.REACT_APP_BACKEND_SERVER + "/" + shortName + "/call/" + _id);
        const data = await res.json();
        if (data && data.success && data.call && data.call.time) {
          // +1ms because /older fetches strictly-older ($lt), same trick as
          // the shareable Link button — puts this call at the top of the page.
          filter.filterDate = new Date(data.call.time).getTime() + 1;
        }
      } catch (err) {
        console.warn("Could not look up call " + _id + ", falling back to URI time: " + err);
      }
    }

    // is there a Filter set?
    if ((uri.hasOwnProperty('filter-code')) && (uri.hasOwnProperty('filter-type'))) {
      if (!urlOptions) setUrlOptions(true);

      // The Filter is a group
      if (uri['filter-type'] === "group") {
        filter.filterType = 1;
        filter.filterGroupId = uri["filter-code"];
      }

      // The Filter is talkgroups
      if (uri['filter-type'] === 'talkgroup') {
        const tg = uri["filter-code"].split(',').map(Number);
        filter.filterType = 2;
        filter.filterTalkgroups = tg;
      }

      // The Filter is a RID / unit alias search
      if (uri['filter-type'] === 'unit') {
        filter.filterType = 3;
        filter.filterUnit = uri["filter-code"];
      }
    }

    dispatch(setFilter(filter));
  }


  useEffect(() => {
    setStateFromUri();
    return () => {
      stopSocket();
    };
  }, []);

  // Keep the search box text in step with the active filter - so clearing it,
  // picking another filter, or loading a unit filter from the URI all show the
  // right thing in the box.
  useEffect(() => {
    setUnitSearch(filterUnit || "");
  }, [filterUnit]);

  // A From/To range is archive browsing, not live - drop out of live mode when
  // one is applied, the same way picking a date on the calendar does.
  useEffect(() => {
    if (filterTimeframe && (filterTimeframe.from || filterTimeframe.to)) {
      setLive(false);
    }
  }, [filterTimeframe]);




  useEffect(() => {
    socket.on('connect', () => {
      console.log("Socket Connect");
      setIsConnected(true);
    });

    socket.on('disconnect', () => {
      console.log("Socket Disconnect");
      setIsConnected(false);
    });

    socket.on('reconnect', (attempts) => {
      console.log("Socket Reconnected after attempts: " + attempts); // true
      setIsConnected(true);
    })

    socket.on("new message", handleSocketMessage);

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('pong');
    };
  }, []);

  // Load the list whenever the system, the filter, or live/archive changes.
  useEffect(() => {
    dispatch(getCalls({}));
  }, [shortName, filterGroupId, filterTalkgroups, filterUnit, filterRows, filterTimeframe, filterType, filterDate, filterStarred, live])

  // Keep the live subscription pointed at whatever is currently being shown.
  useEffect(() => {
    if (live && isConnected) {
      startSocket();
    }
  }, [shortName, filterGroupId, filterTalkgroups, filterUnit, filterRows, filterTimeframe, filterType, filterDate, filterStarred, isConnected, live])

  // A dropped socket leaves a gap in the feed, which only a reload can fill.
  // Doing that while the user is reading older calls would wipe out the pages
  // they have scrolled through, so hold off until they are back at the top.
  useEffect(() => {
    const reconnected = isConnected && !wasConnectedRef.current;
    wasConnectedRef.current = isConnected;

    if (reconnected && live && (window.scrollY <= SCROLL_TOP_THRESHOLD)) {
      dispatch(getCalls({}));
    }
  }, [isConnected, live])


  // Update the Browser URI when any relevant values change
  useEffect(() => {
    updateUri();
  }, [filterGroupId, filterTalkgroups, filterUnit, filterRows, filterTimeframe, filterType, filterDate, filterStarred, selectCallId, initialCallId])

  useEffect(() => {
    if (!urlOptions && groupsData && (groupsData.length > 0)) {
      setGroupVisible(true);
    }
  }, [groupsData])


  let archiveLabel = "";
  if (filterDate) {
    const filterDateObj = new Date(filterDate);
    archiveLabel = filterDateObj.toLocaleDateString() + " " + filterDateObj.toLocaleTimeString()
  }

  const archive = process.env.REACT_APP_ARCHIVE_DAYS

  let filterLabel = "All"
  const timeframeActive = filterTimeframe && (filterTimeframe.from || filterTimeframe.to);
  if ((filterRows && filterRows.length > 0) || timeframeActive) {
    const count = (filterRows ? filterRows.length : 0) + (timeframeActive ? 1 : 0);
    filterLabel = "Advanced (" + count + ")"
  } else {
    switch (filterType) {
      case 1:
        filterLabel = "Group"
        break;

      case 2:
        filterLabel = "Talkgroups"
        break;
      case 3:
        filterLabel = "Unit"
        break;
      default:
      case 0:
        filterLabel = "All"
    }
  }

  return (
    <div>
      <FilterModal shortName={shortName} open={filterVisible} onClose={handleFilterClose} />
      <CalendarModal open={calendarVisible} onClose={handleCalendarClose} archive={archive} key={shortName} />
      <GroupModal shortName={shortName} open={groupVisible} onClose={handleGroupClose} />

      <Sidebar as={Menu} animation='overlay' inverted vertical visible={sidebarOpened}
        onClick={handlePusherClick} duration={50} width='thin'>
        <Menu.Item onClick={handlePusherClick} >
          <span> </span><Icon name="close" inverted={true} link={true} size='small' /></Menu.Item>
        <Link to="/"><Menu.Item link>Home</Menu.Item></Link>
        <Link to="/systems"><Menu.Item link>Systems</Menu.Item></Link>
        <Link to="/events"><Menu.Item link>Events</Menu.Item></Link>
        <Link to="/about"><Menu.Item link>About</Menu.Item></Link>
        <Link to={`/demo/${shortName}`}><Menu.Item link>New look</Menu.Item></Link>
      </Sidebar>
      <Menu fixed="top">
        <Menu.Item onClick={handleSidebarToggle}>
          <Icon name='sidebar' />
        </Menu.Item>
        <Menu.Item name='filter-btn' onClick={handleFilterToggle}>
          <Icon name="filter" />
          <span className="desktop-only">Filter</span>
          <Label horizontal={true} color="grey" className="desktop-only">{filterLabel}</Label>
        </Menu.Item>
        <Menu.Item name='favorites-btn' onClick={() => setGroupVisible(true)}>
          <Icon name="star" />
          <span className="desktop-only">Favourites</span>
        </Menu.Item>
        <Menu.Item name='rid-search' className="rid-search-item">
          <Input
            size='mini'
            icon={{ name: 'search', link: true, onClick: handleUnitSearchSubmit }}
            placeholder='RID or unit…'
            value={unitSearch}
            onChange={(e) => setUnitSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleUnitSearchSubmit();
            }}
          />
        </Menu.Item>
        <div className="system-title desktop-only">
          {system && system.name}
        </div>
        <Menu.Menu position="right">
          <Menu.Item as={Link} to={`/demo/${shortName}`} name='demo-btn' className="desktop-only" title="Try the new look">
            <Icon name="star outline" />
            <span className="desktop-only">Try the new look</span>
          </Menu.Item>
          <Menu.Item name='atcivity-btn' onClick={handleActivityToggle} active={activityVisible}>
            <Icon name="line graph" />
            <span >Activity</span>
          </Menu.Item>
          <Menu.Item name='archive-btn' onClick={handleCalendarToggle} active={!live}>
            <Icon name="calendar" />
            <span className="desktop-only">
              {
                archiveLabel
                  ? archiveLabel
                  : "Archive"
              }
            </span>
          </Menu.Item>
          <Menu.Item name='live-btn' onClick={handleLiveToggle} active={live}>
            <Icon name="unmute" />
            <span className="desktop-only">Live</span>
          </Menu.Item>
          <NotificationBell socket={socket} />
          {me && me.user && (
            <Menu.Item name='logout-btn' onClick={async () => { await logout(); window.location.reload(); }} title={`Log out ${me.user}`}>
              <Icon name="sign out" />
              <span className="desktop-only">{me.user}</span>
            </Menu.Item>
          )}
        </Menu.Menu>
      </Menu>

      {activityVisible
        ? <Activity navigate={handleActivityNavigate} />
        : <CallPlayer system={system} callsData={callsData} live={live} selectCallId={selectCallId} initialCallId={initialCallId} handleNewer={handleNewer} handleOlder={handleOlder} />
      }
    </div>
  );
}


export default Calls;
